import type { LinkProps } from "@tanstack/react-router"

/**
 * A chip that leaves the conversation instead of sending into it.
 *
 * Not everything the advisor may want next is answerable here. The exam prep
 * runs in its own mode, along a question catalogue and with a progress per
 * learning field — a prompt into the open chat would only get her improvised
 * questions that count towards nothing.
 *
 * `to` carries the router's own link type, so a literal path stays narrow. A
 * plain `string` would widen and `Link` would refuse it — the same trap the
 * navigation in `app-header.tsx` names.
 */
export type CoachPromptZiel = { label: string; to: LinkProps["to"] }

/**
 * A chip that does something of its own before, or instead of, sending.
 *
 * The exam drill answers its opening questions with these: picking an area
 * only selects it, and picking a target stores it for the request before the
 * opening message goes out. `ask` is the thread's own send.
 */
export type CoachPromptAktion = {
  label: string
  aktion: (ask: (text: string) => void) => void
  /** Drawn as the suggested choice. */
  primaer?: boolean
}

export type CoachPrompt =
  | string
  | { label: string; prompt: string }
  | CoachPromptZiel
  | CoachPromptAktion

export const istZiel = (entry: CoachPrompt): entry is CoachPromptZiel =>
  typeof entry === "object" && "to" in entry

export const istAktion = (entry: CoachPrompt): entry is CoachPromptAktion =>
  typeof entry === "object" && "aktion" in entry

/**
 * The opening message of an exam drill, sent on the advisor's behalf so the
 * coach asks the first catalogue question at once. Shared by the start chips
 * on the exam page and the suggestion card in the open chat, so both ways in
 * open the same conversation.
 */
export function pruefungStartPrompt(lernfeldTitel?: string, ziel?: number): string {
  const anzahl = ziel ? ` ${ziel} Fragen` : ""
  return lernfeldTitel
    ? `Frag mich${anzahl} zu „${lernfeldTitel}“ ab. Leg direkt mit der ersten Frage los.`
    : `Misch die Themen und frag mich${anzahl} quer durch alle Lernfelder ab. Leg direkt mit der ersten Frage los.`
}

export const COACH_START_PROMPTS: CoachPrompt[] = [
  {
    label: "Produktwissen vertiefen",
    prompt:
      "Ich möchte mein Wissen zu einem Produkt vertiefen. Frag mich zuerst, um welches Produkt und welche Fragen es geht.",
  },
  {
    label: "Dein nächster Schritt",
    prompt:
      "Ich möchte ein bevorstehendes Kundengespräch vorbereiten. Hilf mir, mein Gesprächsziel, passende Fragen und mögliche Einwände durchzugehen.",
  },
  {
    label: "Situation nachbesprechen",
    prompt:
      "Ich möchte eine erlebte Situation aus meiner Beratung nachbesprechen. Frag mich, was passiert ist, und hilf mir zu überlegen, was ich beim nächsten Mal anders machen kann.",
  },
  // Führt hinüber statt zu fragen: die Abfrage entlang des IVFP-Katalogs
  // findet auf ihrer eigenen Seite statt.
  { label: "Für die IVFP-Prüfung lernen", to: "/pruefung" },
]

export const PREPARATION_START_PROMPTS = [
  {
    label: "Einen guten Einstieg finden",
    prompt:
      "Ich möchte meinen nächsten Kundentermin vorbereiten und einen guten Gesprächseinstieg finden. Frag mich zuerst nach dem Anlass und meinem Ziel.",
  },
  {
    label: "Ein sensibles Thema ansprechen",
    prompt:
      "Ich möchte in einem bevorstehenden Kundengespräch ein sensibles Thema ansprechen. Hilf mir bei der Vorbereitung und frag mich zuerst, worum es geht.",
  },
  {
    label: "Auf Einwände vorbereitet sein",
    prompt:
      "Ich rechne bei meinem nächsten Kundentermin mit Einwänden und möchte passende Antworten vorbereiten. Frag mich zuerst nach der Situation und meinen Bedenken.",
  },
]

export const NACHBEREITUNG_START_PROMPTS = [
  {
    label: "Ein Gespräch, das hakte",
    prompt:
      "Ich möchte ein Gespräch nachbesprechen, das nicht so lief wie gedacht. Frag mich zuerst, worum es ging und wie es gelaufen ist.",
  },
  {
    label: "Ein Einwand, der mich kalt erwischt hat",
    prompt:
      "In einem Gespräch kam ein Einwand, auf den ich nicht vorbereitet war. Frag mich zuerst nach der Situation, bevor du etwas einordnest.",
  },
  {
    label: "Es lief gut. Warum?",
    prompt:
      "Ein Gespräch lief richtig gut und ich möchte verstehen, woran das lag. Frag mich zuerst, was passiert ist.",
  },
]
